import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PenTool, CheckSquare, Calendar, Trash2, Save, Undo, Loader2, AlertCircle } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const SignatureSection = ({
    agreementText = "I confirm that I have read and understood the content of this task/training. I certify that the work submitted is my own and I agree to the terms and conditions.",
    signatureData,
    onSignatureChange,
    isSubmitting = false,
    error,
    readOnly = false,
    showValidationError = false // New prop for external validation
}) => {
    const [isChecked, setIsChecked] = useState(!!signatureData);
    const [isUploading, setIsUploading] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const sigCanvas = useRef(null);
    const containerRef = useRef(null);

    // If signatureData is a URL or base64, we treat it as signed
    const isSigned = signatureData && (signatureData.startsWith('http') || signatureData.startsWith('data:'));

    // Resize canvas to fit container (important for mobile)
    const resizeCanvas = useCallback(() => {
        if (containerRef.current && sigCanvas.current) {
            const container = containerRef.current;
            const canvas = sigCanvas.current.getCanvas();

            // Get the container dimensions
            const rect = container.getBoundingClientRect();
            const width = rect.width;
            const height = Math.max(160, Math.min(200, window.innerHeight * 0.25)); // Responsive height

            // Only resize if dimensions actually changed (compare with current canvas size)
            const currentWidth = parseInt(canvas.style.width) || 0;
            const currentHeight = parseInt(canvas.style.height) || 0;

            if (width > 0 && (Math.abs(width - currentWidth) > 1 || Math.abs(height - currentHeight) > 1)) {
                // Save current signature data before resize
                const currentData = !sigCanvas.current.isEmpty() ? sigCanvas.current.toDataURL() : null;

                // Set canvas dimensions
                canvas.width = width * window.devicePixelRatio;
                canvas.height = height * window.devicePixelRatio;
                canvas.style.width = `${width}px`;
                canvas.style.height = `${height}px`;

                // Scale context for retina displays
                const ctx = canvas.getContext('2d');
                ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

                // Restore signature if there was one
                if (currentData) {
                    sigCanvas.current.fromDataURL(currentData);
                }
            }
        }
    }, []); // No dependencies - uses refs directly

    // Initial resize and window resize handler
    useEffect(() => {
        // Small delay to ensure container is rendered
        const initialTimeout = setTimeout(resizeCanvas, 50);

        const handleResize = () => {
            // Debounce resize
            setTimeout(resizeCanvas, 100);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            clearTimeout(initialTimeout);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [resizeCanvas]);

    // Sync check state with signature data
    useEffect(() => {
        if (signatureData) {
            setIsChecked(true);
        }
    }, [signatureData]);

    const handleCheckChange = (e) => {
        setIsChecked(e.target.checked);
        if (!e.target.checked) {
            onSignatureChange('');
            if (sigCanvas.current) {
                sigCanvas.current.clear();
            }
            setIsEmpty(true);
        }
    };

    const clearSignature = () => {
        if (sigCanvas.current) {
            sigCanvas.current.clear();
        }
        setIsEmpty(true);
        onSignatureChange('');
        toast.success('Signature cleared');
    };

    const handleSignatureEnd = () => {
        if (sigCanvas.current) {
            setIsEmpty(sigCanvas.current.isEmpty());
        }
    };

    const handleSignatureBegin = () => {
        if (!isChecked && sigCanvas.current) {
            sigCanvas.current.clear();
        }
    };

    const saveSignature = async () => {
        if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
            toast.error("Please sign before saving");
            return;
        }

        setIsUploading(true);
        try {
            // Get base64 signature directly from canvas
            const dataURL = sigCanvas.current.toDataURL('image/png');

            // Convert to blob for upload
            const res = await fetch(dataURL);
            const blob = await res.blob();
            const file = new File([blob], `signature_${Date.now()}.png`, { type: "image/png" });

            const formData = new FormData();
            formData.append('files', file);

            const token = localStorage.getItem('token');
            const response = await api.post('/files/upload-proof', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success && response.data.files.length > 0) {
                const signatureUrl = response.data.files[0].file_url;
                onSignatureChange(signatureUrl);
                toast.success('Signature saved!');
            } else {
                toast.error('Failed to upload signature');
            }
        } catch (error) {
            console.error('Signature upload error:', error);
            toast.error('Failed to save signature');
        } finally {
            setIsUploading(false);
        }
    };

    // Determine if we should show an error state
    const hasError = error || (showValidationError && !isSigned && isEmpty);
    const errorMessage = error || (showValidationError && !isSigned ? 'Signature required' : null);

    return (
        <div className={`border rounded-xl overflow-hidden transition-all ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
            {/* Header */}
            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${hasError ? 'bg-red-100 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                        <PenTool className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className={`font-bold ${hasError ? 'text-red-800' : 'text-gray-800'}`}>
                            {readOnly ? 'Signature' : 'Sign to Acknowledge'}
                        </h3>
                        {errorMessage && (
                            <p className="text-xs text-red-600 font-medium mt-0.5 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {errorMessage}
                            </p>
                        )}
                    </div>
                </div>
                {!readOnly && (
                    <div className="text-xs font-medium text-gray-400 flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-gray-100 shadow-sm">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date().toLocaleDateString()}
                    </div>
                )}
            </div>

            <div className="p-4 sm:p-6">
                {/* Agreement Checkbox */}
                <div className="flex items-start gap-3 mb-6 bg-gray-50/80 p-4 rounded-lg border border-gray-100">
                    <div className="pt-0.5 flex-shrink-0">
                        <input
                            type="checkbox"
                            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50"
                            checked={isChecked}
                            onChange={handleCheckChange}
                            id="signature-agreement"
                            disabled={isSubmitting || isSigned || readOnly}
                        />
                    </div>
                    <label
                        htmlFor="signature-agreement"
                        className={`text-sm text-gray-600 leading-relaxed ${!readOnly ? 'cursor-pointer' : ''} select-none`}
                    >
                        {agreementText}
                    </label>
                </div>

                {/* Signature Area */}
                <div className={`relative transition-all duration-300 ${(!isChecked && !readOnly) ? 'opacity-50 grayscale pointer-events-none' : 'opacity-100'}`}>

                    {/* Canvas Container */}
                    <div
                        ref={containerRef}
                        className={`relative ${readOnly ? '' : 'border-2 border-dashed rounded-xl bg-gray-50'} ${hasError && !isSigned ? 'border-red-300' : 'border-gray-300'
                            }`}
                    >
                        {/* Show existing signature if signed */}
                        {isSigned ? (
                            <div className="relative w-full flex items-center justify-center bg-white rounded-xl overflow-hidden border border-gray-100" style={{ minHeight: '160px' }}>
                                <img
                                    src={signatureData}
                                    alt="Signature"
                                    className="max-h-40 max-w-full object-contain p-4"
                                />
                                {!readOnly && (
                                    <button
                                        onClick={() => {
                                            onSignatureChange('');
                                            setIsEmpty(true);
                                        }}
                                        className="absolute top-2 right-2 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors shadow-sm"
                                        title="Clear Signature"
                                        disabled={isSubmitting}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ) : readOnly ? (
                            <div className="w-full flex flex-col items-center justify-center bg-gray-50 rounded-xl border border-gray-200 text-gray-400" style={{ minHeight: '128px' }}>
                                <PenTool className="w-8 h-8 mb-2 opacity-50" />
                                <p className="text-sm">No signature provided</p>
                            </div>
                        ) : (
                            <div className="relative w-full touch-none" style={{ minHeight: '160px' }}>
                                {/* Placeholder text when empty */}
                                {isEmpty && isChecked && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                                        <span className="text-gray-300 text-lg font-medium select-none">
                                            Sign here
                                        </span>
                                    </div>
                                )}

                                {/* Checkbox reminder overlay */}
                                {!isChecked && (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                                        <div className="bg-white/90 backdrop-blur-[2px] px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-gray-500 border border-gray-200 flex items-center gap-2">
                                            <CheckSquare className="w-4 h-4" />
                                            Please check the box above to enable signing
                                        </div>
                                    </div>
                                )}

                                {/* Signature Canvas */}
                                <SignatureCanvas
                                    ref={sigCanvas}
                                    penColor="black"
                                    minWidth={1.5}
                                    maxWidth={3}
                                    velocityFilterWeight={0.7}
                                    canvasProps={{
                                        className: 'w-full rounded-xl cursor-crosshair touch-none',
                                        style: {
                                            touchAction: 'none',
                                            minHeight: '160px'
                                        }
                                    }}
                                    onBegin={handleSignatureBegin}
                                    onEnd={handleSignatureEnd}
                                />

                                {/* Clear Button - More prominent */}
                                {!isEmpty && (
                                    <button
                                        onClick={clearSignature}
                                        className="absolute top-2 right-2 flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors shadow-sm border border-gray-200"
                                        title="Clear Signature"
                                        type="button"
                                        disabled={isSubmitting}
                                    >
                                        <Undo className="w-4 h-4" />
                                        <span className="text-sm font-medium">Clear</span>
                                    </button>
                                )}

                                {/* Signature line hint */}
                                <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                                    <div className="border-b border-gray-200"></div>
                                    <span className="text-[10px] text-gray-300 font-mono mt-1 block text-right">
                                        Your Signature
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    {!isSigned && isChecked && !readOnly && (
                        <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
                            {!isEmpty && (
                                <button
                                    onClick={clearSignature}
                                    disabled={isUploading || isSubmitting}
                                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 font-medium sm:hidden"
                                >
                                    <Undo className="w-4 h-4" />
                                    Clear Signature
                                </button>
                            )}
                            <button
                                onClick={saveSignature}
                                disabled={isUploading || isSubmitting || isEmpty}
                                className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed shadow-sm font-medium min-h-[44px]"
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        Save Signature
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Validation hint */}
                    {!isSigned && !readOnly && isEmpty && isChecked && (
                        <p className="text-xs text-gray-500 mt-3 text-center">
                            Draw your signature above using your mouse, finger, or stylus
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SignatureSection;
